import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stack,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { request } from '../utils/request';
import ReusableHeader from '../components/Header/ReusableHeader';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    mobile_number: '',
    address: '',
    department: '',
    location: '',
    roles: [],
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    const data = await request('users', { method: 'get' });
    setUsers(data);
  };

  const fetchRoles = async () => {
    const data = await request('roles', { method: 'get' });
    setRoles(data);
  };

  const handleOpen = (user = null) => {
    if (user) {
      setFormData({
        id: user._id,
        username: user.username,
        password: '',
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        mobile_number: user.mobile_number || '',
        address: user.address || '',
        department: user.department || '',
        location: user.location || '',
        roles: (user.roles || []).map(r => r._id || r),
        source: user.source,
        authMethod: user.authMethod,
      });
      setIsEditing(true);
    } else {
      setFormData({
        id: null,
        username: '',
        password: '',
        first_name: '',
        last_name: '',
        email: '',
        mobile_number: '',
        address: '',
        department: '',
        location: '',
        roles: [],
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.username || !formData.email) {
      showSnack('First Name, Last Name, Username, and Email are required.', 'error');
      return;
    }
    if (!isEditing && !formData.password) {
      showSnack('Password is required.', 'error');
      return;
    }

    try {
      const isAd = isEditing && (formData.authMethod === 'ad' || formData.source === 'ad');
      const payload = { ...formData, roles: formData.roles };
      if (isAd) {
        delete payload.password;
      }
      const opts = isEditing
        ? { method: 'put', params: formData.id, body: payload }
        : { method: 'post', body: payload };
      await request('users', opts);
      fetchUsers();
      handleClose();
      showSnack(isEditing ? 'User updated successfully' : 'User created successfully', 'success');
    } catch (err) {
      let errorMessage = 'Failed to save user';
      if (err?.errors && typeof err.errors === 'object') {
        errorMessage = Object.values(err.errors).join(', ');
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      showSnack(errorMessage, 'error');
    }
  };

  return (
    <Box p={2}>
      {/* Header and Add Button */}
      <ReusableHeader
        title="Users"
        breadcrumbs={[{ label: "Users" }]}
        actionButton={
          <Button
            variant="contained"
            size="small"
            sx={{ backgroundColor: '#00bf63', '&:hover': { backgroundColor: '#00794d' }, py: 0.5, px: 2 }}
            onClick={() => handleOpen()}
          >
            Add User
          </Button>
        }
      />

      {/* Source Filter */}
      <Box sx={{ my: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="source-filter-label">Source</InputLabel>
          <Select
            labelId="source-filter-label"
            value={sourceFilter}
            label="Source"
            onChange={e => setSourceFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="Manual">Manual</MenuItem>
            <MenuItem value="SCIM">SCIM</MenuItem>
            <MenuItem value="Active Directory">Active Directory</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Users Table */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f0f7f5' }}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users
                .filter(u => {
                  if (!sourceFilter) return true;
                  const sf = sourceFilter.toLowerCase();
                  const s = (u.source || '').toLowerCase();
                  if (sf === 'active directory' || sf === 'ad') return s === 'ad';
                  if (sf === 'scim') return s === 'scim';
                  if (sf === 'manual') return s === 'manual' || !s;
                  return true;
                })
                .map((u, idx) => {
                  const src = (u.source || '').toLowerCase();
                  const badgeLabel = src === 'ad' ? 'Active Directory' : src === 'scim' ? 'SCIM' : 'Manual';
                  return (
                    <TableRow key={u._id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{u.username}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.department}</TableCell>
                      <TableCell>
                        {u.roles?.map(r => r.name).join(', ') || '-'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" label={badgeLabel} />
                          {u.mustChangePassword && (
                            <Chip
                              size="small"
                              label="Password change required"
                              color="warning"
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleOpen(u)}
                          sx={{ color: '#00bf63', p: 0.5 }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* User Form Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
     <DialogTitle
  sx={{
    backgroundColor: '#00bf63',
    color: '#fff',
    fontSize: '1rem',
    py: 1,
    textAlign: 'center' // This centers the text
  }}
>
  {isEditing ? 'Edit User' : 'Add User'}
</DialogTitle>

        <DialogContent sx={{ p: 2 , mt: '10px'  }}>
          <Stack spacing={1}>
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Password"
              name="password"
              type={isEditing && (formData.authMethod === 'ad' || formData.source === 'ad') ? 'text' : 'password'}
              value={
                isEditing && (formData.authMethod === 'ad' || formData.source === 'ad')
                  ? 'Password is managed by Active Directory'
                  : formData.password
              }
              disabled={isEditing && (formData.authMethod === 'ad' || formData.source === 'ad')}
              helperText={
                isEditing && (formData.authMethod === 'ad' || formData.source === 'ad')
                  ? 'Password is managed by Active Directory'
                  : undefined
              }
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="Mobile Number"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                name="roles"
                value={formData.roles}
                onChange={handleChange}
                input={<OutlinedInput label="Roles" />}
                renderValue={selected =>
                  roles
                    .filter(r => selected.includes(r._id))
                    .map(r => r.name)
                    .join(', ')
                }
              >
                {roles.map(r => (
                  <MenuItem key={r._id} value={r._id}>
                    <Checkbox checked={formData.roles.includes(r._id)} size="small" />
                    <ListItemText primary={r.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button onClick={handleClose} sx={{ color: '#00bf63', fontSize: '0.875rem', px: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            size="small"
            sx={{ backgroundColor: '#00bf63', '&:hover': { backgroundColor: '#00794d' }, fontSize: '0.875rem', px: 2 }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert
          onClose={handleSnackClose}
          severity={snackSeverity}
          sx={{ width: '100%' }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
